import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Checkbox, Input } from 'semantic-ui-react'

import { Avatar, IMAGE_SIZES, Trophy } from 'images'
import { actions, selectors } from 'teams'
import { ProgressBar, SimpleButton, Tooltip } from 'ui'

import { matchTeamLike } from 'pages/sports/soccer/MatchPageActions'

import type { ChangeEvent, FC, FormEvent, MouseEventHandler } from 'react'

const ITEMS_PER_PAGE = 49

export interface ListProps {
  owner?: { id: string }
}

const List: FC<ListProps> = ({ owner }) => {
  const dispatch = useDispatch()
  const viewer = useSelector((state: { auth: { usr?: { id: string } } }) => state.auth.usr)
  const { data, loading, pagination } = useSelector(selectors.list)
  const [userId, setUserId] = useState((owner || viewer)?.id)
  const [search, setSearch] = useState('')

  useEffect(() => {
    const filters: Parameters<typeof actions.teamsList>[0] = { per_page: ITEMS_PER_PAGE, search }
    if (userId) {
      filters.userId = userId
    }
    dispatch(actions.teamsList(filters))
  }, [dispatch, search, userId])

  const toggleUser = (_event: FormEvent<HTMLInputElement>) => {
    setUserId((prevValue) => (prevValue ? undefined : (owner || viewer)?.id))
  }

  const onChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value)
  }

  const onTeamLike: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    dispatch(matchTeamLike(event.currentTarget.value, true))
  }

  const onTeamUnfollow: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    dispatch(matchTeamLike(event.currentTarget.value, false))
  }

  return (
    <div className="List bg-white p-2">
      <div className="flex flex-wrap items-center p-2 gap-2">
        <b>Filtros: </b>
        <Input label="Buscar" onChange={onChangeSearch} value={search} />
        {!!(owner || viewer)?.id && (
          <Checkbox checked={!!userId} label="Solo mis equipos" onChange={toggleUser} toggle />
        )}
      </div>
      {loading && (
        <div className="text-sm border-t bg-greyLightest">
          <div className="px-2">Buscando equipos...</div>
          <ProgressBar value={100} />
        </div>
      )}
      <div className="grid grid-cols-1 640:grid-cols-2 1024:grid-cols-3 1280:grid-cols-4 gap-1 p-2">
        {data.map((team) => {
          const isFollowing = Number(team.following?.length || 0) > 0
          return (
            <div
              key={team.id}
              className="border hover:border-green focus-within:border-green hover:shadow focus-within:shadow rounded"
            >
              <div className="flex gap-1 items-center px-1 pt-1">
                <Link className="shrink-0 font-bold truncate !no-underline" tabIndex={-1} to={`/sst/${team.id}`}>
                  <Avatar
                    fallbackIconName="shield"
                    size="4em"
                    src={team.avatar}
                    srcImageSize={IMAGE_SIZES.FIT_100_100}
                  />
                </Link>
                <div className="grow flex flex-col overflow-hidden">
                  <Link className="font-bold truncate" to={`/sst/${team.id}`}>
                    {team.name}
                  </Link>
                  <div className="text-sm truncate text-greyDark">
                    <b>{team.nickname}</b>
                  </div>
                  <div className="text-xs truncate text-greyDark">
                    {[team.category?.name, team.discipline?.name, team.gender?.name].filter(Boolean).join(', ')}
                  </div>
                  <div className="text-sm py-1">
                    {team.relevant_tournaments?.length === 0 && <span>Sin torneos</span>}
                    {team.relevant_tournaments?.length === 1 && (
                      <div key={team.relevant_tournaments[0]?.id} className="flex gap-1 items-center">
                        <Trophy
                          className="text-lg"
                          src={team.relevant_tournaments[0]?.avatar}
                          srcImageSize={IMAGE_SIZES.FIT_45_45}
                        />
                        <span className="truncate">{team.relevant_tournaments[0]?.name}</span>
                      </div>
                    )}
                    {team.relevant_tournaments?.length > 1 && (
                      <Tooltip
                        tooltip={
                          <div className="flex flex-col gap-1 p-1">
                            {team.relevant_tournaments.slice(0, 4).map((tournament) => (
                              <div key={tournament.id} className="flex gap-1 items-center">
                                <Trophy
                                  className="text-lg"
                                  src={tournament.avatar}
                                  srcImageSize={IMAGE_SIZES.FIT_45_45}
                                />
                                {tournament.name}
                              </div>
                            ))}
                            {team.relevant_tournaments.length > 4 && (
                              <div className="flex gap-1 items-center">{team.relevant_tournaments.length - 4} más</div>
                            )}
                          </div>
                        }
                      >
                        <span>{team.relevant_tournaments.length} torneos</span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex justify-end items-center gap-2 text-sm">
                    <SimpleButton
                      icon={isFollowing ? 'check' : 'feed'}
                      onClick={isFollowing ? onTeamUnfollow : onTeamLike}
                      primary={isFollowing}
                      value={team.id}
                    >
                      {isFollowing ? 'Siguiendo' : 'Seguir'}
                    </SimpleButton>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="p-2 text-right">
        {pagination && (
          <>
            Mostrando <b>{pagination.to || 0}</b> de <b>{pagination.total}</b>
          </>
        )}
      </div>
    </div>
  )
}

export default List
